'use strict';

import { CoreRepository } from '../CoreRepository';
import { Ind } from '../../Entities/Ind/Ind';
import { IndCollection } from '../../Collections/Ind/IndCollection';
import type { APICriteria, APIOptions } from '@api/API/CoreAPI';

/**
 * Searchable fields
 */
export type IndCriteria = {
  ownerId: number;
  indStatus: string;
  indLetter: string;
  indName: string;
  indFirstname: string;
  indLastname: string;
  synchedVendor: string;
  indDesignation: string;
  policyPremiumIncrease: string;
  householdIncome: string;
  indFlagged: string;
  indMedicaidLevel: string;
  indMedicaidPlan: string;
  irmaa: string;
  lep: string;
  indMedicareNumber: string;
  employer: string;
  pharmacyName: string;
  indDrugName: string;
  prefLang: string;
  soaState: string;
  isConsent: string;
  soaCreatedDate: string;
  indDobDate: string;
  loginDateTime: string;
  indDob: string;
  policyEffective: string;
  policyAppDate: string;
  leadDate: string;
  dateTimeCreated: string;
  lastUpdateTime: string;
  indMedicaidRenewal: string;
  indSocialStartDate: string;
  policyTermDate: string;
  policyMaturityDate: string;
  indAge: string;
  indDobMonth: string;
  indBirthday: string;
  policyAnniversary: string;
  indSource: string;
  indReferral: string;
  indReferralTo: string;
  indPreferredContactMethod: string;
  loa: string;
  indAddressCity: string;
  indAddressState: string;
  indAddressZip: string;
  indAddressCounty: string;
  indAddressCommunity: string;
  policyHospNtwk: string;
  policyPcpNtwk: string;
  indProviderNetworks: string;
  indProviderLastname: string;
  indProviderFirstname: string;
  indProviderFacility: string;
  indSpap: string;
  tracker: string;
  leadDispo: string;
  leadDismiss: number;
  leadStatus: string;
  policyType: string;
  policyStatus: string;
  policyWrittenAs: string;
  policyLives: string;
  policyPremium: string;
  companyName: string;
  policyAor: string;
  policyExchangeStatus: string;
  enrollmentSource: string;
  policyElectionPeriod: string;
  policyUsingSubsidy: string;
  policyPlan: string;
  planTerming: string;
  planServiceDisruption: string;
  planNumber: string;
  isDeleted: boolean;
};

export type IndAPIInclude =
  | 'spouse'
  | 'ind.spouse'
  | 'prefLang'
  | 'indPortalLog'
  | 'emails'
  | 'spouse.emails'
  | 'addresses'
  | 'ind.addresses'
  | 'spouse.addresses'
  | 'planYearReviews'
  | 'policies'
  | 'policies.company'
  | 'policies.plan'
  | 'providers'
  | 'pharmacies'
  | 'rx'
  | 'connections'
  | 'dependents'
  | 'soas'
  | 'flags'
  | 'lead'
  | 'vendorSession'
  | 'medicare'
  | 'medicaid'
  | 'spap'
  | 'health'
  | 'social'
  | 'employer'
  | 'profileUrl'
  | 'phones'
  | 'spouse.phones'
  | 'alternateIds'
  | 'loa'
  | 'owner';

/**
 * Criteria & include
 */
export type IndAPICriteria = APICriteria<IndCriteria>;

export type IndAPIOptions = APIOptions<IndAPICriteria, IndAPIInclude>;

export class IndRepository extends CoreRepository {
  public getInd(individualId: number, options: IndAPIOptions = null) {
    if (!options) {
      options = {};
    }

    return this.webAPI.getInd(individualId, options).then((response) => {
      const user = new Ind();
      user.hydrate(response);

      return user;
    });
  }

  public getIndByNumber(individualId: string, options: IndAPIOptions = null) {
    if (!options) {
      options = {};
    }

    return this.webAPI.getIndByNumber(individualId, options).then((response) => {
      const user = new Ind();
      user.hydrate(response);

      return user;
    });
  }

  public getInds(options: IndAPIOptions = null, criteria: IndAPICriteria = null) {
    if (!options) {
      options = {};
    }

    if (criteria) {
      options.criteria = criteria;
    }

    return this.webAPI.getInds(options).then((response) => {
      const collection = new IndCollection();

      response.data.forEach((collectionItemData) => {
        const collectionItem = new Ind();
        collectionItem.hydrate(collectionItemData);

        collection.addItem(collectionItem);
      });

      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }

  public getIndsTotal(options: IndAPIOptions = null, criteria: IndAPICriteria = null) {
    if (!options) {
      options = {};
    }

    if (criteria) {
      options['criteria'] = criteria;
    }

    return this.webAPI.getIndsTotal(options).then((response) => {
      const collection = new IndCollection();

      collection.setTotal(response.total);

      return collection;
    });
  }

  public createInd(userId: number, payload: any[]) {
    return this.webAPI.createInd(userId, payload).then((response) => {
      const ind = new Ind();
      ind.hydrate(response);

      return ind;
    });
  }

  public deleteInds(userId: number, individualIds: number[]) {
    return this.webAPI.deleteInds(userId, individualIds).then((response) => {
      return response;
    });
  }

  public deleteInd(individualId: number[]) {
    return this.webAPI.deleteInd(individualId).then((response) => {
      return response;
    });
  }

  public updateInds(userId: number, payload: any[]) {
    return this.webAPI.updateInds(userId, payload).then((response) => {
      return response;
    });
  }

  public updateInd(individualId: number, payload: any): Promise<Ind> {
    return this.webAPI.updateInd(individualId, payload).then((response) => {
      const ind = new Ind();
      ind.hydrate(response);

      return ind;
    });
  }

  public getSampleInd() {
    return this.webAPI.getSampleInd().then((response) => {
      const user = new Ind();
      user.hydrate(response);

      return user;
    });
  }

  //  getInd
}
